/**
 * Retry logic utilities for Mews API
 * 
 * Implements retry strategies for:
 * - 429 Too Many Requests: Uses Retry-After header or exponential backoff
 * - 408 Request Timeout: Returns error with guidance to reduce request load
 * 
 * Reference: https://mews-systems.gitbook.io/booking-engine-guide/booking-engine-api/guidelines/requests#request-limits
 */

/**
 * Retry configuration options
 */
export interface RetryConfig {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries?: number;
  /** Initial delay in milliseconds for exponential backoff (default: 1000) */
  initialDelayMs?: number;
  /** Maximum delay in milliseconds for exponential backoff (default: 30000) */
  maxDelayMs?: number;
  /** Multiplier for exponential backoff (default: 2) */
  backoffMultiplier?: number;
  /** Whether to use Retry-After header when available (default: true) */
  useRetryAfterHeader?: boolean;
  /** Custom retry condition function */
  shouldRetry?: (status: number, attempt: number) => boolean;
}

/**
 * Default retry configuration
 */
const DEFAULT_RETRY_CONFIG: Required<RetryConfig> = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 30000,
  backoffMultiplier: 2,
  useRetryAfterHeader: true,
  shouldRetry: (status: number) => {
    // Retry on 429 (Too Many Requests) and 500 (Internal Server Error)
    // Do not retry on 408 (Request Timeout) - requires reducing request load
    return status === 429 || status === 500;
  },
};

/**
 * Calculates delay for exponential backoff
 * 
 * @param attempt - Current retry attempt (0-indexed)
 * @param config - Retry configuration
 * @returns Delay in milliseconds
 */
function calculateExponentialBackoff(
  attempt: number,
  config: Required<RetryConfig>
): number {
  const delay = config.initialDelayMs * Math.pow(config.backoffMultiplier, attempt);
  return Math.min(delay, config.maxDelayMs);
}

/**
 * Extracts Retry-After value from response headers
 * 
 * @param headers - Response headers
 * @returns Retry-After value in milliseconds, or null if not present/invalid
 */
function extractRetryAfter(headers: Headers): number | null {
  const retryAfter = headers.get('Retry-After');
  if (!retryAfter) {
    return null;
  }

  // Retry-After can be either a number of seconds or an HTTP-date
  // We'll handle both cases
  const seconds = parseInt(retryAfter, 10);
  if (!isNaN(seconds) && seconds > 0) {
    return seconds * 1000; // Convert to milliseconds
  }

  // Try parsing as HTTP-date (RFC 7231)
  const date = new Date(retryAfter);
  if (!isNaN(date.getTime())) {
    const now = Date.now();
    const delay = date.getTime() - now;
    return delay > 0 ? delay : null;
  }

  return null;
}

/**
 * Waits for the specified duration
 * 
 * @param ms - Milliseconds to wait
 */
function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Retry function with exponential backoff and Retry-After header support
 * 
 * @param fn - Function to retry (should return a Response or throw an error)
 * @param config - Retry configuration
 * @returns Promise resolving to the function result
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  config: RetryConfig = {}
): Promise<T> {
  const finalConfig: Required<RetryConfig> = {
    ...DEFAULT_RETRY_CONFIG,
    ...config,
    shouldRetry: config.shouldRetry || DEFAULT_RETRY_CONFIG.shouldRetry,
  };

  let lastError: unknown;
  let lastResponse: Response | undefined;

  for (let attempt = 0; attempt <= finalConfig.maxRetries; attempt++) {
    try {
      const result = await fn();
      
      // If result is a Response, check if it's an error that should be retried
      if (result instanceof Response) {
        const status = result.status;
        
        // If successful, return immediately
        if (result.ok) {
          return result as T;
        }

        // Check if we should retry this status code
        if (finalConfig.shouldRetry(status, attempt) && attempt < finalConfig.maxRetries) {
          lastResponse = result;
          
          // Calculate delay
          let delayMs: number;
          
          if (status === 429 && finalConfig.useRetryAfterHeader) {
            // Use Retry-After header if available
            const retryAfter = extractRetryAfter(result.headers);
            if (retryAfter !== null) {
              delayMs = retryAfter;
            } else {
              // Fall back to exponential backoff
              delayMs = calculateExponentialBackoff(attempt, finalConfig);
            }
          } else {
            // Use exponential backoff
            delayMs = calculateExponentialBackoff(attempt, finalConfig);
          }

          // Log retry attempt (only in development)
          if (import.meta.env.DEV) {
            console.warn(
              `[Mews API] Retry attempt ${attempt + 1}/${finalConfig.maxRetries} after ${status} error. ` +
              `Waiting ${delayMs}ms before retry...`
            );
          }

          // Wait before retrying
          await wait(delayMs);
          continue;
        }

        // Not retryable or max retries reached, return the error response
        return result as T;
      }

      // Non-Response result, return immediately
      return result;
    } catch (error) {
      lastError = error;
      
      // For network errors, retry if we haven't exceeded max retries
      if (attempt < finalConfig.maxRetries) {
        const delayMs = calculateExponentialBackoff(attempt, finalConfig);
        
        if (import.meta.env.DEV) {
          console.warn(
            `[Mews API] Retry attempt ${attempt + 1}/${finalConfig.maxRetries} after network error. ` +
            `Waiting ${delayMs}ms before retry...`
          );
        }

        await wait(delayMs);
        continue;
      }

      // Max retries reached, throw the error
      throw error;
    }
  }

  // This should never be reached, but TypeScript needs it
  if (lastError) {
    throw lastError;
  }
  
  if (lastResponse) {
    return lastResponse as T;
  }

  throw new Error('Retry logic failed unexpectedly');
}

/**
 * Determines if a 408 Request Timeout error should be retried
 * 
 * According to Mews documentation, 408 errors indicate the request puts too much
 * demand on the system. The best solution is to reduce the load (e.g., split requests
 * into smaller time periods) rather than retrying with the same request.
 * 
 * @param status - HTTP status code
 * @returns false (408 errors should not be retried automatically)
 */
export function shouldRetry408(status: number): boolean {
  // 408 Request Timeout should not be retried - requires reducing request load
  return false;
}

/**
 * Creates an error message for 408 Request Timeout with guidance
 * 
 * @returns Error message with mitigation guidance
 */
export function get408ErrorMessage(): string {
  return (
    'Request Timeout: The request puts too much demand on the system. ' +
    'Please reduce the load by splitting requests into smaller time periods. ' +
    'For example, if requesting data over several days returns a 408 error, ' +
    'try multiple requests, each for a separate day.'
  );
}
